import React from 'react';
import { useLocation } from 'react-router';
import { AppParamsContext, IAppParams } from '../../AppParams';
import { stripQueryString } from './brandPresencePaths';

/** The filters a screen of the area shows, as the query the host page keeps them in. */
export interface IBrandPresenceScreenQuery {
  pathname: string;
  query: Record<string, string>;
}

/** The address a screen reads its filters from: a query object where the host page hands one over, and the address itself. */
export interface IBrandPresenceScreenAddress {
  query: unknown;
  path: string | undefined;
}

/**
 * The one address a screen's filters are read from at any moment.
 *
 * Once the host page's address has caught up with an in-frame navigation, it is the only source —
 * the screen's changes go there through `update-query`, so a filter removed from it stays removed.
 * Until then the frame's own address fills the gap, because that navigation just wrote it. The
 * host page's address can name this screen while still missing the query the navigation carried —
 * at the handover it lays its own query onto the path it is handed — so it has caught up only once
 * it hands a query back, or there was none to wait for. What is never read is a
 * mixture of the two: a key the host page's address has dropped must not fall through to an older
 * copy, so once the host page has caught up it stays the source for as long as the frame stays on
 * this screen.
 */
export function useBrandPresenceScreenAddress(): IBrandPresenceScreenAddress {
  const appParams = React.useContext(AppParamsContext) as IAppParams | null;
  const location = useLocation();

  const requestedQuery: unknown = appParams?.pageOptions?.query;
  const requestedPath = (appParams?.pageOptions?.brandPresencePath || appParams?.pageOptions?.governancePath) as string | undefined;
  const hostNamesThisScreen = typeof requestedPath === 'string' && stripQueryString(requestedPath) === location.pathname;
  const hostCarriesQuery =
    hostNamesThisScreen &&
    (requestedPath.includes('?') ||
      (!!requestedQuery && typeof requestedQuery === 'object' && Object.keys(requestedQuery as object).length > 0));

  const hostCaughtUpRef = React.useRef({ pathname: '', caughtUp: false });
  if (hostCaughtUpRef.current.pathname !== location.pathname) {
    hostCaughtUpRef.current = { pathname: location.pathname, caughtUp: false };
  }
  if (hostNamesThisScreen && (hostCarriesQuery || !location.search)) {
    hostCaughtUpRef.current.caughtUp = true;
  }
  const hostIsTheSource = hostNamesThisScreen && hostCaughtUpRef.current.caughtUp;

  return React.useMemo(
    () =>
      hostIsTheSource
        ? { query: requestedQuery, path: requestedPath }
        : { query: undefined, path: `${location.pathname}${location.search}` },
    [hostIsTheSource, requestedQuery, requestedPath, location.pathname, location.search]
  );
}

/**
 * Where the screens of the area report the filters they keep in the host page's address.
 *
 * Content Inventory, Content Health and AI answers send their filters to the host page with
 * `update-query` and read them back from the path it hands over, so the frame's own address never
 * carries them. The Settings row's Back reads them from here to return to the screen as the reader
 * left it. Outside the area there is no provider and a report goes nowhere.
 */
export const BrandPresenceScreenQueryContext = React.createContext<React.MutableRefObject<IBrandPresenceScreenQuery | undefined> | null>(
  null
);

/** Reports the query that restores the screen in view, each time it changes. */
export function useReportBrandPresenceScreenQuery(query: Record<string, string>): void {
  const reported = React.useContext(BrandPresenceScreenQueryContext);
  const { pathname } = useLocation();

  React.useEffect(() => {
    if (reported) {
      reported.current = { pathname, query };
    }
  }, [pathname, query, reported]);
}

/** The query as the search part of a link: empty when there is nothing to carry. */
export function toBrandPresenceSearch(query: Record<string, string>): string {
  const search = new URLSearchParams(query).toString();
  return search ? `?${search}` : '';
}

/** The search part of the one address, so a link out of a screen carries the filters it shows. */
export function searchOfBrandPresenceScreenAddress(address: IBrandPresenceScreenAddress): string {
  if (address.query && typeof address.query === 'object') {
    return toBrandPresenceSearch(address.query as Record<string, string>);
  }
  const path = address.path || '';
  const queryStart = path.indexOf('?');
  return queryStart === -1 ? '' : path.slice(queryStart);
}
